import {
  BusinessOutlined,
  PhoneOutlined,
  AccountBalanceOutlined,
  PersonOutlined,
  Edit,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { Box, Button, Chip, Grid } from "@mui/material";
import { BASE_CONSTANTS } from "constants/baseConstants";
import FormDrawer from "components/Shared/FormDrawer";
import InformationTile from "components/Shared/InformationTile";
import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useEditEntityMutation,
  useDetailEntityQuery,
} from "store/services/entityApi";
import { EntityAdditionalInfoForm } from "./EntityAdditionalInfoForm";
import { useEntityDetailContext } from "contexts/EntityDetailContext";

export const EntityAdditionalInfo = () => {
  const { id } = useParams();
  const [showForm, setShowForm] = React.useState(false);

  const { entityDetail } = useEntityDetailContext();
  const [updateBranding, { isLoading: updatingBranding }] =
    useEditEntityMutation();
  const { refetch } = useDetailEntityQuery(id);

  const handleClose = () => {
    setShowForm(false);
  };

  const handleUpdatePreferences = async (data) => {
    try {
      await updateBranding({
        ...data,
        id: id,
      }).unwrap();
      toast.success("Entity Preferences successfully updated!");
      refetch();
      handleClose();
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update entity!");
      console.error("handleUpdatePreferences error:", err);
    }
  };

  return (
    <>
      <Box
        sx={{
          p: BASE_CONSTANTS.PADDING_MD,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          startIcon={<Edit />}
          variant="outlined"
          onClick={() => setShowForm(true)}
        >
          EDIT
        </Button>
      </Box>

      <Box padding={BASE_CONSTANTS.PADDING_MD}>
        <Grid container spacing={4}>
          <Grid item md={4}>
            <InformationTile
              icon={() => <BusinessOutlined color="primary" />}
              title="Location"
              subtitle={entityDetail?.location ?? "N/A"}
            />
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <PhoneOutlined color="primary" />}
              title="Telephone Number"
              subtitle={entityDetail?.telNo ?? "N/A"}
            />
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <AccountBalanceOutlined color="primary" />}
              title="PAN Number"
              subtitle={entityDetail?.additionalInfo?.panNumber?.value ?? "N/A"}
              extra={
                <Chip
                  size="small"
                  icon={
                    entityDetail?.additionalInfo?.panNumber?.display ? (
                      <Visibility />
                    ) : (
                      <VisibilityOff />
                    )
                  }
                  label={
                    entityDetail?.additionalInfo?.panNumber?.display
                      ? "Visible"
                      : "Hidden"
                  }
                  sx={{
                    backgroundColor: entityDetail?.additionalInfo?.panNumber
                      ?.display
                      ? "success.main"
                      : "error.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.65rem",
                    height: "20px",
                    padding: "0 4px",
                    "& .MuiChip-label": {
                      padding: "0 6px",
                    },
                  }}
                />
              }
            />
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <AccountBalanceOutlined color="primary" />}
              title="VAT Number"
              subtitle={entityDetail?.additionalInfo?.vatNumber?.value ?? "N/A"}
              extra={
                <Chip
                  size="small"
                  icon={
                    entityDetail?.additionalInfo?.vatNumber?.display ? (
                      <Visibility />
                    ) : (
                      <VisibilityOff />
                    )
                  }
                  label={
                    entityDetail?.additionalInfo?.vatNumber?.display
                      ? "Visible"
                      : "Hidden"
                  }
                  sx={{
                    backgroundColor: entityDetail?.additionalInfo?.vatNumber
                      ?.display
                      ? "success.main"
                      : "error.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.65rem",
                    height: "20px",
                    padding: "0 4px",
                    "& .MuiChip-label": {
                      padding: "0 6px",
                    },
                  }}
                />
              }
            />
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <AccountBalanceOutlined color="primary" />}
              title="Tax ID"
              subtitle={entityDetail?.additionalInfo?.taxId?.value ?? "N/A"}
              extra={
                <Chip
                  size="small"
                  icon={
                    entityDetail?.additionalInfo?.taxId?.display ? (
                      <Visibility />
                    ) : (
                      <VisibilityOff />
                    )
                  }
                  label={
                    entityDetail?.additionalInfo?.taxId?.display
                      ? "Visible"
                      : "Hidden"
                  }
                  sx={{
                    backgroundColor: entityDetail?.additionalInfo?.taxId
                      ?.display
                      ? "success.main"
                      : "error.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.65rem",
                    height: "20px",
                    padding: "0 4px",
                    "& .MuiChip-label": {
                      padding: "0 6px",
                    },
                  }}
                />
              }
            />
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <AccountBalanceOutlined color="primary" />}
              title="Registration Number"
              subtitle={
                entityDetail?.additionalInfo?.registrationNumber?.value ?? "N/A"
              }
              extra={
                <Chip
                  size="small"
                  icon={
                    entityDetail?.additionalInfo?.registrationNumber
                      ?.display ? (
                      <Visibility />
                    ) : (
                      <VisibilityOff />
                    )
                  }
                  label={
                    entityDetail?.additionalInfo?.registrationNumber?.display
                      ? "Visible"
                      : "Hidden"
                  }
                  sx={{
                    backgroundColor: entityDetail?.additionalInfo
                      ?.registrationNumber?.display
                      ? "success.main"
                      : "error.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.65rem",
                    height: "20px",
                    padding: "0 4px",
                    "& .MuiChip-label": {
                      padding: "0 6px",
                    },
                  }}
                />
              }
            />
          </Grid>
          <Grid item md={4}>
            <InformationTile
              icon={() => <PersonOutlined color="primary" />}
              title="Name of Proprietors"
              subtitle={entityDetail?.proprietorsName ?? "N/A"}
            />
          </Grid>
        </Grid>
      </Box>

      <FormDrawer
        title="Edit Information"
        open={showForm}
        onClose={handleClose}
      >
        <EntityAdditionalInfoForm
          isBusy={updatingBranding}
          onSubmit={handleUpdatePreferences}
          onCancel={handleClose}
          initialValues={{
            location: entityDetail?.location || "",
            telNo: entityDetail?.telNo || "",
            proprietorsName: entityDetail?.proprietorsName || "",
            additionalInfo: {
              registrationNumber: {
                value:
                  entityDetail?.additionalInfo?.registrationNumber?.value || "",
                display:
                  entityDetail?.additionalInfo?.registrationNumber?.display ??
                  true,
              },
              panNumber: {
                value: entityDetail?.additionalInfo?.panNumber?.value || "",
                display:
                  entityDetail?.additionalInfo?.panNumber?.display ?? true,
              },
              vatNumber: {
                value: entityDetail?.additionalInfo?.vatNumber?.value || "",
                display:
                  entityDetail?.additionalInfo?.vatNumber?.display ?? true,
              },
              taxId: {
                value: entityDetail?.additionalInfo?.taxId?.value || "",
                display: entityDetail?.additionalInfo?.taxId?.display ?? true,
              },
            },
          }}
        />
      </FormDrawer>
    </>
  );
};
